import {Drawer} from 'components/customer/Product/entity/Drawer';
import {
    getFaceHeight,
    readjustLastAutoHeight,
} from 'components/customer/Product/Drawer/helpers';

export const AUTO = 1;
export const FIXED = 0;

// The fieldset declares a total that already has the gaps taken out of it, so
// the cabinet helpers are handed no gap and no margins to subtract again.
const NO_GAP = 0;
const NO_MARGIN = 0;

// Face heights are rounded to a decimal place on the way in, so a face is only
// counted as differing from its share by more than that rounding.
const SHARE_TOLERANCE = 0.1;

export interface Face {
    index: number;
    height: number;
    isAuto: boolean;
    // the one face still absorbing the remainder cannot itself be set
    isLocked: boolean;
}

const toDrawers = (heights: number[], auto: boolean[]): Drawer[] =>
    heights.map((height, index) => ({
        drawer_face_height: height,
        drawer_style: auto[Number(index)] ? AUTO : FIXED,
    }));

// Gives every auto face an equal share of what the fixed ones leave behind.
const share = (heights: number[], auto: boolean[], total: number): number[] => {
    const drawers = toDrawers(heights, auto);

    drawers.forEach((drawer, index) => {
        if (drawer.drawer_style === AUTO) {
            drawer.drawer_face_height = getFaceHeight(
                drawers,
                index,
                total,
                NO_MARGIN,
                NO_MARGIN,
                NO_GAP
            );
        }
    });

    return readjustLastAutoHeight(
        drawers,
        total,
        NO_MARGIN,
        NO_MARGIN,
        NO_GAP
    ).map((drawer) => drawer.drawer_face_height);
};

// Which faces the customer decided on, worked out from the heights alone: the
// product has nowhere to record it, and a face saved at anything other than its
// share of the space was set on purpose. The last face always absorbs.
export const seedAuto = (heights: number[], total: number): boolean[] => {
    const auto = heights.map(() => true);
    const last = heights.length - 1;

    // Settle on the faces that reproduce the heights as saved: start with all
    // of them sharing, and whenever one sits away from the share it would be
    // given, take it as set and share what is left over again. The last face
    // is never taken, since something has to absorb the remainder.
    for (;;) {
        const sharing = auto.filter(Boolean).length;
        const spent = heights.reduce(
            (sum, height, index) => (auto[Number(index)] ? sum : sum + height),
            0
        );
        const share = (total - spent) / sharing;

        let furthest = -1;
        let by = SHARE_TOLERANCE;

        heights.forEach((height, index) => {
            if (!auto[Number(index)] || index === last) return;

            const away = Math.abs(height - share);

            if (away > by) {
                by = away;
                furthest = index;
            }
        });

        if (furthest < 0) return auto;

        auto[Number(furthest)] = false;
    }
};

export const toFaces = (heights: number[], auto: boolean[]): Face[] => {
    const autoCount = auto.filter(Boolean).length;

    return heights.map((height, index) => ({
        index,
        height,
        isAuto: auto[Number(index)],
        isLocked: auto[Number(index)] && autoCount <= 1,
    }));
};

export interface FaceState {
    heights: number[];
    auto: boolean[];
}

// Keeps every decision and gives the autos a fresh share, for when the space
// itself changes rather than what the customer asked of it.
export const reshare = (
    {heights, auto}: FaceState,
    total: number
): FaceState => ({auto, heights: share(heights, auto, total)});

export const shareEqually = (count: number, total: number): FaceState => {
    const auto = new Array<boolean>(count).fill(true);

    return {
        auto,
        heights: share(new Array<number>(count).fill(0), auto, total),
    };
};

// Fixes the one face and lets only the autos move, so a height set on purpose
// is never shifted by an edit somewhere else.
export const setFaceHeight = (
    {heights, auto}: FaceState,
    total: number,
    index: number,
    height: number
): FaceState => {
    // nothing would be left to absorb the change
    if (auto[Number(index)] && auto.filter(Boolean).length <= 1) {
        return {heights, auto};
    }

    const next = auto.map((isAuto, position) =>
        position === index ? false : isAuto
    );
    const edited = heights.map((value, position) =>
        position === index ? height : value
    );

    return {auto: next, heights: share(edited, next, total)};
};

export const setFaceAuto = (
    {heights, auto}: FaceState,
    total: number,
    index: number
): FaceState => {
    const next = auto.map((isAuto, position) =>
        position === index ? true : isAuto
    );

    return {auto: next, heights: share(heights, next, total)};
};

// Takes a preset shape — a height per face, null for the ones left on auto —
// and gives the autos what the set faces leave behind.
export const applyShape = (
    shape: (number | null)[],
    total: number
): FaceState => {
    const auto = shape.map((height) => height === null);
    const heights = shape.map((height) => height ?? 0);

    return {auto, heights: share(heights, auto, total)};
};
